import { ReactNode } from 'react';
import { SRD } from '../../index';
import { DUALITY_DICE, HOPE_DIE, FEAR_DIE, ACTION_ROLL } from '../../character/content/templates';

interface Section {
  title: string;
  content: ReactNode;
}

export default function IntroContent() {
  return (
    <>
      {([
        {
          title: 'What is Daggerheart?',
          content: (
            <>
              <p>Daggerheart is a tabletop roleplaying game for one Game Master ("GM") and 2-5 players. Each game session lasts about 2-4 hours, and Daggerheart can be played as a one-shot or a multi-session campaign of any length.</p>
              <p>During a session of Daggerheart the GM describes situations, narrates events, and controls any adversaries or obstacles that the Player Characters ("PCs") encounter. The players, in turn, roleplay their PCs' reactions to the scenario presented by the GM.</p>
            </>
          ),
        },
        {
          title: 'The Duality Dice',
          content: (
            <>
              <p>If the outcome of a player's action depends on fate or fortune, the GM calls for an {ACTION_ROLL}. When a player makes an action roll, they utilize {DUALITY_DICE}, two differently colored 12-sided dice ("d12s") representing {HOPE_DIE} and {FEAR_DIE}.</p>
              <p>The Duality Dice are rolled, relevant modifiers are added to the results, and the total is compared to a Difficulty set by the GM. If the total meets or beats the Difficulty, the player succeeds. If it's lower, they fail.</p>
              <p>In addition, the situation changes based on which Duality Die rolls higher, either giving the player helpful Hope tokens or generating terrifying Fear tokens for the GM.</p>
            </>
          ),
        },
        {
          title: 'The Golden Rule',
          content: (
            <>
              <p>The most important rule of Daggerheart is to make the game your own. The rules included in this <a href={SRD}>SRD</a> are designed to help you enjoy the experience at the table, but everyone has a different approach to interpreting rules and telling stories.</p>
              <p>The rules should never get in the way of the story you want to tell, the characters you want to play, or the adventures you want to have. As long as your group agrees, everything can be adjusted to fit your play style. If there's a rule you'd rather ignore or modify, feel free to implement any change with your table's consent.</p>
            </>
          ),
        },
        {
          title: 'Rulings Over Rules',
          content: (
            <>
              <p>While playing Daggerheart, the GM and players should always prioritize rulings over rules. This <a href={SRD}>SRD</a> offers answers for many questions your table may have about the game, but it won't answer all of them. When you're in doubt about how a rule applies, the GM should make a ruling that aligns with the narrative.</p>
              <p>As a narrative-focused game, Daggerheart is not a place where technical, out-of-context interpretations of the rules are encouraged. Everything should flow back to the fiction, and the GM has the authority and responsibility to make rulings about how rules are applied to underscore that fiction.</p>
            </>
          ),
        },
      ] as Section[]).map((section) => (
        <div key={section.title} className="section-block">
          <h2>{section.title}</h2>
          {section.content}
        </div>
      ))}

      <aside className="intro-aside">
        <h3>What is this <a href={SRD}>SRD</a>?</h3>
        <p>
          The Daggerheart <a href={SRD}>SRD</a> (System Reference Document) is a repository of the mechanical elements of the
          Daggerheart system, edited and organized for clarity and quick reference. It is published under the
          Darrington Press Community Gaming License.
        </p>
      </aside>
    </>
  );
}
